import {Suspense, useMemo} from "react";
import {FlagsProvider} from "flagged";
import {Helmet, HelmetProvider} from "react-helmet-async";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import {useZendeskPrefill, useZendeskSuggestedPosts, useInvitation, useUpdatedTerms, useToggle} from "~/hooks";
import {AnyObject} from "~/model/helperTypes/generic";
import {useAppSelector, strongSelectMaker} from "~/modules/redux";
import {PrivateModals} from "~/containers/modals/PrivateModals";
import {AuthRight} from "~/api/generated";
import {withAbilities} from "~/modules/roles";
import NotFound from "~/pages/notFound/NotFound";
import {withCompanyInfo} from "~/modules/company";
import {MobileHeader} from "~/containers/header/MobileHeader/MobileHeader";
import {Sidebar} from "~/containers/sidebar/Sidebar";

import {withEssentialPrivateData} from "../hocs/withEssentialPrivateData";

export interface PrivateRouteWrapperProps {
  SuspenseFallbackComponent: React.ComponentType<any>;
  PageComponent: React.ComponentType<any>;
  componentProps: AnyObject;
  zendeskRelatedLabels?: string[];
  accessRight?: AuthRight;
}

const PrivateRouteContent: React.FC<PrivateRouteWrapperProps> = ({
  SuspenseFallbackComponent,
  PageComponent,
  componentProps,
  zendeskRelatedLabels,
  accessRight,
}) => {
  useZendeskPrefill();
  useZendeskSuggestedPosts();
  useInvitation();
  useUpdatedTerms();

  const [mobileMenuOpen, mobileMenuToggle] = useToggle(false);
  const theme = useTheme();
  const isLowerThanLg = useMediaQuery(theme.breakpoints.down("lg"));

  const maker = useAppSelector(strongSelectMaker);

  const features = useMemo(() => {
    let nextFeatures: AnyObject = {
      [process.env.BEMAKERS_ENV!]: true,
    };

    maker.features.forEach((feature) => {
      nextFeatures = {
        ...nextFeatures,
        [feature]: true,
      };
    });

    return nextFeatures;
  }, [maker.features]);

  if (accessRight && !maker.rights.includes(accessRight)) return <NotFound />;

  return (
    <FlagsProvider features={features}>
      <HelmetProvider>
        <Helmet>{zendeskRelatedLabels && <meta name="keywords" content={zendeskRelatedLabels.join(", ")} />}</Helmet>
      </HelmetProvider>

      <Box minHeight="100vh">
        {isLowerThanLg && <MobileHeader onMenuIconClick={mobileMenuToggle} />}
        <Box display="flex">
          <Sidebar mobileMenuOpen={mobileMenuOpen} mobileMenuToggle={mobileMenuToggle} />
          <Box flex="auto">
            <Suspense fallback={<SuspenseFallbackComponent />}>
              <PageComponent {...componentProps} />
            </Suspense>
          </Box>
        </Box>
      </Box>
      <PrivateModals />
    </FlagsProvider>
  );
};

export const PrivateRouteWrapper = withEssentialPrivateData(withAbilities(withCompanyInfo(PrivateRouteContent)));
